import { IShowType } from "common/broadcast/_stores/broadcast/enums";
import { ChatModeEnum, IChatTab } from "common/broadcast/_stores/chat/enums";
import NodeChatStore from "common/broadcast/_stores/nodechat/NodeChatStore";
import { logger } from "library/core/utility";
import { VAD } from "./VAD";
import { broadcastStore, nodeChatSock, profileStore } from "core/stores";
import CookieStorage from "library/core/utility/cookie-storage";

const logPrefix = "[LiveTranslationService]:";
const VOICE_IDX_COOKIE_KEY = "liveTranslation_selectedVoiceIdx";

export interface VoiceOption {
  url: string;
  name: string;
  idx: number;
  displayNumber?: number;
}

export interface VoiceOptions {
  female_options: VoiceOption[];
  male_options: VoiceOption[];
}

export class LiveTranslationService {
  ws?: WebSocket;
  opusEncoder?: any;
  vad?: any;
  audioContext?: any;
  audioProcessor?: any;
  audioProcessorType?: any;
  audioSource?: any;
  vadVoice?: any;
  speechBuffer?: any;
  isRecordingAudio: boolean = false;
  messageBuffer: string[] = [];
  messageBufferTimer: any = null;
  recordingStatusInterval: any = null;
  // Push-to-talk state
  pushToTalkActive: boolean = false;
  pushToTalkCooldownActive: boolean = false;
  pushToTalkAutoStopTimer: any = null;
  pushToTalkCooldownTimer: any = null;
  pushToTalkStatusInterval: any = null;
  pushToTalkActiveEndAt: number | null = null;
  pushToTalkCooldownEndAt: number | null = null;
  // Session & processing endpoint
  sessionId: string = `sess-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
  processBaseUrl: string = "livestreaming.prod.cams.run";
  // TTS Voice options
  voiceOptions: VoiceOptions | null = null;
  selectedVoiceIdx: number = 0;
  currentPreviewAudio: HTMLAudioElement | null = null;

  // Subscribers to receive structured live translation data
  private liveTranslationDataSubscribers: Array<
    (data: {
      ok?: boolean;
      audioUrl?: string;
      transcriptEs?: string;
      translationEn?: string;
      timings?: any;
      raw?: any;
    }) => void
  > = [];
  // Streaming mode: 'text' uses Opus frames; 'audio' uses MediaRecorder WebM/Opus
  streamMode: "text" | "audio" = "audio";
  mediaStream?: MediaStream;
  mediaRecorder?: MediaRecorder;

  constructor(private nodeChatStore: NodeChatStore) {
    this.log = this.log.bind(this);
    this.connectToServer = this.connectToServer.bind(this);
    this.initOpusEncoder = this.initOpusEncoder.bind(this);
    this.encodeAndSendOpus = this.encodeAndSendOpus.bind(this);
    this.processPCMBuffer = this.processPCMBuffer.bind(this);
    this.createAudioProcessor = this.createAudioProcessor.bind(this);
    this.startAudioRecording = this.startAudioRecording.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.startPushToTalk = this.startPushToTalk.bind(this);
    this.stopPushToTalk = this.stopPushToTalk.bind(this);
    this.setStreamMode = this.setStreamMode.bind(this);
    this.startAudioModeRecording = this.startAudioModeRecording.bind(this);
    this.stopAudioModeRecording = this.stopAudioModeRecording.bind(this);
    this.openWsForSession = this.openWsForSession.bind(this);

    // Load saved voice index after all methods are bound
    const savedIdx = this.loadSelectedVoiceIdx();
    if (savedIdx !== null) {
      this.selectedVoiceIdx = savedIdx;
    }
  }

  log = (...params: any[]) => {
    logger.log(logPrefix, ...params);
  };

  connectToServer() {
    // Text mode uses a single WS for Opus frames; audio mode opens WS lazily per PTT
    if (this.streamMode === "audio") {
      this.log("audio mode detected, WS will open on PTT start");
      return;
    }
    // Text mode WS
    const serverUrl =
      "wss://mc.adultfriendfinder.com/CD/RTServer/1.0/wstester?device-id=web_test_client&client-id=web_test_client&pwsid=70020679_31218";
    let wsUrl = serverUrl;
    try {
      const u = new URL(serverUrl);
      u.searchParams.set("sessionId", this.sessionId);
      wsUrl = u.toString();
    } catch (_) {}
    this.ws = new WebSocket(wsUrl);
    this.ws.binaryType = "arraybuffer";
    this.ws.onopen = () => {
      this.log("websocket connected.");
      this.startAudioRecording();
    };
    this.ws.onclose = () => {
      this.log("websocket disconnected.");
    };
    this.ws.onerror = e => {
      this.log("error", { e });
    };
    this.ws.onmessage = event => {
      if (typeof event.data === "string") {
        try {
          const data = JSON.parse(event.data);
          if (data.session_id) {
            this.log("onmessage", { data });
          }
          if (data.text && data.translated_text) {
            this.log("Received original text:", data.text, "info");
          }
          if (data.translated_text) {
            this.log("Received translated text:", data.translated_text, "info");
            this.messageBuffer.push(data.translated_text);
            // While Push-to-Talk is active, accumulate and do not flush mid-stream.
            if (!this.pushToTalkActive && this.streamMode === "audio") {
              if (this.messageBuffer.join("").length >= 100) {
                this.flushMessageBuffer();
              } else {
                if (this.messageBufferTimer)
                  clearTimeout(this.messageBufferTimer);
                this.messageBufferTimer = setTimeout(() => {
                  this.flushMessageBuffer();
                }, 5 * 1000);
              }
            }
          }
          if (event.data) {
            this.log("Received Message:", event.data, "info");
          }
        } catch (e) {
          if (event.data) {
            this.log("Received Message:", event.data, "info");
            this.log(e, "error");
          }
        }
      } else {
        this.log("Received Binary Data", "info");
      }
    };
  }

  // Opus Encoder Initialization (copied and adapted)
  initOpusEncoder(ModuleInstance: any) {
    try {
      if (this.opusEncoder) return true;
      const mod = ModuleInstance;
      const sampleRate = 16000,
        channels = 1,
        application = 2048;
      this.opusEncoder = {
        channels,
        sampleRate,
        frameSize: 960,
        maxPacketSize: 4000,
        module: mod,
        init: function () {
          try {
            const encoderSize = mod._opus_encoder_get_size(this.channels);
            this.encoderPtr = mod._malloc(encoderSize);
            if (!this.encoderPtr)
              throw new Error("Cannot allocate encoder memory");
            const err = mod._opus_encoder_init(
              this.encoderPtr,
              this.sampleRate,
              this.channels,
              application
            );
            if (err < 0)
              throw new Error(`Opus encoder initialization failed: ${err}`);
            mod._opus_encoder_ctl(this.encoderPtr, 4002, 16000); // OPUS_SET_BITRATE
            mod._opus_encoder_ctl(this.encoderPtr, 4010, 5); // OPUS_SET_COMPLEXITY
            mod._opus_encoder_ctl(this.encoderPtr, 4016, 1); // OPUS_SET_DTX
            return true;
          } catch (error) {
            if (this.encoderPtr) {
              mod._free(this.encoderPtr);
              this.encoderPtr = null;
            }
            return false;
          }
        },
        encode: function (pcmData) {
          if (!this.encoderPtr) if (!this.init()) return null;
          try {
            const mod = this.module;
            const pcmPtr = mod._malloc(pcmData.length * 2);
            for (let i = 0; i < pcmData.length; i++)
              mod.HEAP16[(pcmPtr >> 1) + i] = pcmData[i];
            const outPtr = mod._malloc(this.maxPacketSize);
            const encodedLen = mod._opus_encode(
              this.encoderPtr,
              pcmPtr,
              this.frameSize,
              outPtr,
              this.maxPacketSize
            );
            if (encodedLen < 0)
              throw new Error(`Opus encoding failed: ${encodedLen}`);
            const opusData = new Uint8Array(encodedLen);
            for (let i = 0; i < encodedLen; i++)
              opusData[i] = mod.HEAPU8[outPtr + i];
            mod._free(pcmPtr);
            mod._free(outPtr);
            return opusData;
          } catch (error) {
            return null;
          }
        },
        destroy: function () {
          if (this.encoderPtr) {
            this.module._free(this.encoderPtr);
            this.encoderPtr = null;
          }
        },
      };
      return this.opusEncoder.init();
    } catch (error) {
      this.log(`Failed to create Opus encoder: ${error.message}`, "error");
      return false;
    }
  }

  encodeAndSendOpus(pcmData: any = null) {
    if (!this.opusEncoder) {
      this.log("Opus encoder not initialized", "error");
      return;
    }
    try {
      if (pcmData) {
        const opusData = this.opusEncoder.encode(pcmData);
        if (opusData && opusData.length > 0) {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(opusData.buffer);
          }
        }
      }
    } catch (error) {
      this.log(`Opus encoding error: ${error.message}`, "error");
    }
  }

  processPCMBuffer(buffer) {
    if (!this.isRecordingAudio) return;
    // Only forward audio frames to encoder when PTT is active
    if (!this.pushToTalkActive) return;
    const samplesPerFrame = 960;
    let pcmBuffer = buffer;
    while (pcmBuffer.length >= samplesPerFrame) {
      const frame = new Int16Array(pcmBuffer.slice(0, samplesPerFrame));
      pcmBuffer = pcmBuffer.slice(samplesPerFrame);
      this.encodeAndSendOpus(frame);
    }
    if (pcmBuffer.length > 0) {
      const padded = new Int16Array(samplesPerFrame);
      padded.set(pcmBuffer);
      this.encodeAndSendOpus(padded);
    }
  }

  async createAudioProcessor() {
    if (!this.audioContext)
      this.audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)({ sampleRate: 16000 });
    try {
      if (this.audioContext.audioWorklet) {
        const audioProcessorCode = `
                class AudioRecorderProcessor extends AudioWorkletProcessor {
                    constructor() {
                        super();
                        this.frameSize = 960;
                        this.buffer = new Int16Array(this.frameSize);
                        this.bufferIndex = 0;
                        this.isRecordingAudio = true;
                        this.port.onmessage = (event) => {
                            if (event.data.command === 'stop') this.isRecordingAudio = false;
                        };
                    }
                    process(inputs) {
                        if (!this.isRecordingAudio) return true;
                        const input = inputs[0][0];
                        if (!input) return true;
                        for (let i = 0; i < input.length; i++) {
                            if (this.bufferIndex >= this.frameSize) {
                                this.port.postMessage({ type: 'buffer', buffer: this.buffer.slice(0) });
                                this.bufferIndex = 0;
                            }
                            this.buffer[this.bufferIndex++] = Math.max(-32768, Math.min(32767, Math.floor(input[i] * 32767)));
                        }
                        return true;
                    }
                }
                registerProcessor('audio-recorder-processor', AudioRecorderProcessor);
            `;
        const blob = new Blob([audioProcessorCode], {
          type: "application/javascript",
        });
        const url = URL.createObjectURL(blob);
        await this.audioContext.audioWorklet.addModule(url);
        URL.revokeObjectURL(url);
        const audioProcessor = new AudioWorkletNode(
          this.audioContext,
          "audio-recorder-processor"
        );
        audioProcessor.port.onmessage = event => {
          if (event.data.type === "buffer") {
            this.processPCMBuffer(event.data.buffer);
          }
        };
        this.log("Using AudioWorklet to process audio", "success");
        return { node: audioProcessor, type: "worklet" };
      } else {
        this.log(
          "AudioWorklet not available, using ScriptProcessorNode as fallback",
          "warning"
        );
        const frameSize = 4096;
        const scriptProcessor = this.audioContext.createScriptProcessor(
          frameSize,
          1,
          1
        );
        scriptProcessor.onaudioprocess = event => {
          if (!this.isRecordingAudio) return;
          const input = event.inputBuffer.getChannelData(0);
          const buffer = new Int16Array(input.length);
          for (let i = 0; i < input.length; i++) {
            buffer[i] = Math.max(
              -32768,
              Math.min(32767, Math.floor(input[i] * 32767))
            );
          }
          this.processPCMBuffer(buffer);
        };
        return { node: scriptProcessor, type: "processor" };
      }
    } catch (error) {
      this.log(`Failed to create audio processor: ${error.message}`, "error");
      return null;
    }
  }

  async startAudioRecording() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.log("connectToServer Please connect to the server first", "error");
      return;
    }
    const ModuleInstance = (window as any).ModuleInstance;
    if (this.isRecordingAudio) return;
    this.isRecordingAudio = true;
    if (!this.vad)
      this.vad = new VAD({
        mode: VAD.Mode.NORMAL,
      });
    if (!this.initOpusEncoder(ModuleInstance)) {
      this.log("Opus encoder initialization failed", "error");
      return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { sampleRate: 16000, channelCount: 1 },
    });
    this.audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)({
      sampleRate: 16000,
    });
    const processorResult: any = await this.createAudioProcessor();
    this.audioProcessor = processorResult.node;
    this.audioProcessorType = processorResult.type;
    this.audioSource = this.audioContext.createMediaStreamSource(stream);
    this.audioSource.connect(this.audioProcessor);
    this.vadVoice = false;
    this.speechBuffer = [];
    if (this.audioProcessorType === "worklet" && this.audioProcessor.port) {
      this.audioProcessor.port.postMessage({ command: "start" });
    }
    this.log("Recording started", "info");
  }

  flushMessageBuffer() {
    if (this.messageBuffer.length === 0) return;
    const combined = this.messageBuffer.join(". ");

    // Check for active whisper - either activeMemberToWhisper or activeChatTab
    let whisperMember = this.nodeChatStore.activeMemberToWhisper;

    // If not using activeMemberToWhisper, check if we're on a whisper tab
    if (!whisperMember) {
      const activeChatTab = this.nodeChatStore.activeChatTab;
      const isChatTabWhisper = !Object.values(IChatTab).includes(
        activeChatTab as IChatTab
      );
      if (isChatTabWhisper) {
        const whisperHash = this.nodeChatStore.whisperHash;
        whisperMember = whisperHash[activeChatTab]?.member;
      }
    }

    if (whisperMember?.id) {
      nodeChatSock.sendWhisper(
        `[Live Translation] ${combined}`,
        whisperMember.id
      );
    } else {
      this.nodeChatStore.sendMessage(
        `[Live Translation] ${combined}`,
        ChatModeEnum.PUBLIC
      );
    }
    this.messageBuffer = [];
    if (this.messageBufferTimer) {
      clearTimeout(this.messageBufferTimer);
      this.messageBufferTimer = null;
    }
  }

  disconnect() {
    this.stopAudioModeRecording();
    if (this.ws) {
      try {
        this.ws.close();
      } catch (_) {}
      this.ws = undefined;
      this.log("WebSocket disconnected");
    }
    if (this.messageBufferTimer) {
      clearTimeout(this.messageBufferTimer);
      this.messageBufferTimer = null;
    }
    if (this.recordingStatusInterval) {
      clearInterval(this.recordingStatusInterval);
      this.recordingStatusInterval = null;
    }
    if (this.pushToTalkAutoStopTimer) {
      clearTimeout(this.pushToTalkAutoStopTimer);
      this.pushToTalkAutoStopTimer = null;
    }
    if (this.pushToTalkCooldownTimer) {
      clearTimeout(this.pushToTalkCooldownTimer);
      this.pushToTalkCooldownTimer = null;
    }
    if (this.pushToTalkStatusInterval) {
      clearInterval(this.pushToTalkStatusInterval);
      this.pushToTalkStatusInterval = null;
    }
    this.pushToTalkActive = false;
    this.pushToTalkCooldownActive = false;
    this.pushToTalkActiveEndAt = null;
    this.pushToTalkCooldownEndAt = null;
    this.messageBuffer = [];
    // Release audio resources
    if (this.audioSource && this.audioProcessor) {
      this.audioSource.disconnect(this.audioProcessor);
    }
    if (this.audioProcessor) {
      this.audioProcessor.disconnect();
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = undefined;
    }
    this.audioProcessor = undefined;
    this.audioProcessorType = undefined;
    this.audioSource = undefined;
    if (this.mediaStream) {
      try {
        this.mediaStream.getTracks().forEach(t => t.stop());
      } catch (_) {}
    }
    this.mediaStream = undefined;
    this.vad = undefined;
    this.vadVoice = undefined;
    this.speechBuffer = undefined;
    this.isRecordingAudio = false;
  }

  get isActive() {
    const isWsOpen = !!(this.ws && this.ws.readyState === WebSocket.OPEN);
    if (this.streamMode === "audio") {
      // In audio mode, consider translation "live" when WS is connected
      return isWsOpen;
    }
    // In text mode, require both recorder active and WS open
    return !!(this.isRecordingAudio && isWsOpen);
  }

  subscribeToRecordingStatus(callback: (isActive: boolean) => void) {
    if (this.recordingStatusInterval) {
      clearInterval(this.recordingStatusInterval);
    }
    this.recordingStatusInterval = setInterval(
      () => callback(this.isActive),
      1000
    );
  }

  // Push-to-talk controls
  startPushToTalk(
    maxActiveMs: number = 30_000,
    cooldownMs: number = 5_000
  ): boolean {
    if (this.streamMode === "text") {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
      if (!this.isRecordingAudio) return false;
    }
    if (this.pushToTalkCooldownActive) return false;
    if (this.pushToTalkActive) return true;
    this.pushToTalkActive = true;
    this.pushToTalkActiveEndAt = Date.now() + maxActiveMs;
    if (this.pushToTalkAutoStopTimer)
      clearTimeout(this.pushToTalkAutoStopTimer);
    this.pushToTalkAutoStopTimer = setTimeout(() => {
      this.stopPushToTalk(cooldownMs);
    }, maxActiveMs);
    if (this.streamMode === "audio") {
      // Per-PTT session, open WS and start MediaRecorder when open
      this.sessionId = `sess-${Date.now()}-${Math.random()
        .toString(16)
        .slice(2)}`;
      this.openWsForSession().then(() => this.startAudioModeRecording());
    }
    return true;
  }

  stopPushToTalk(cooldownMs: number = 5_000): void {
    if (!this.pushToTalkActive && this.pushToTalkCooldownActive) return;
    if (this.pushToTalkAutoStopTimer) {
      clearTimeout(this.pushToTalkAutoStopTimer);
      this.pushToTalkAutoStopTimer = null;
    }
    this.pushToTalkActive = false;
    this.pushToTalkActiveEndAt = null;
    this.pushToTalkCooldownActive = true;
    this.pushToTalkCooldownEndAt = Date.now() + cooldownMs;
    if (this.pushToTalkCooldownTimer)
      clearTimeout(this.pushToTalkCooldownTimer);
    this.pushToTalkCooldownTimer = setTimeout(() => {
      this.pushToTalkCooldownActive = false;
      this.pushToTalkCooldownEndAt = null;
      this.pushToTalkCooldownTimer = null;
    }, cooldownMs);
    // After stopping PTT, flush the full accumulated translation
    if (this.messageBufferTimer) {
      clearTimeout(this.messageBufferTimer);
      this.messageBufferTimer = null;
    }
    // Close websocket like the HTML sample so the server finalizes the session,
    // then process the session and flush the combined translation to chat.
    const triggerProcess = () => {
      // Small grace period to allow any final server fragments to arrive
      setTimeout(() => {
        this.flushMessageBuffer();
        this.processSession();
      }, 300);
    };
    if (this.streamMode === "audio") {
      // Stop MediaRecorder; then close WS, then process
      const doCloseAndProcess = () => {
        if (this.ws) {
          try {
            const wsRef = this.ws;
            wsRef.addEventListener(
              "close",
              () => {
                this.ws = undefined;
                triggerProcess();
              },
              { once: true }
            );
            wsRef.close();
          } catch (_) {
            triggerProcess();
          }
        } else {
          triggerProcess();
        }
      };
      this.stopAudioModeRecording();
      // MediaRecorder.onstop fires sync/async; close WS afterwards
      setTimeout(doCloseAndProcess, 0);
    } else {
      // Text mode: don't close WS here
      triggerProcess();
    }
  }

  subscribeToPushToTalkStatus(
    callback: (status: { active: boolean; cooldown: boolean }) => void
  ) {
    if (this.pushToTalkStatusInterval) {
      clearInterval(this.pushToTalkStatusInterval);
    }
    this.pushToTalkStatusInterval = setInterval(() => {
      const now = Date.now();
      const activeRemainingMs = this.pushToTalkActiveEndAt
        ? Math.max(0, this.pushToTalkActiveEndAt - now)
        : 0;
      const cooldownRemainingMs = this.pushToTalkCooldownEndAt
        ? Math.max(0, this.pushToTalkCooldownEndAt - now)
        : 0;
      callback({
        active: this.pushToTalkActive,
        cooldown: this.pushToTalkCooldownActive,
        activeRemainingMs,
        cooldownRemainingMs,
      } as any);
    }, 500);
  }

  private async processSession() {
    try {
      await profileStore.ensureModelStreamId();
      if (!profileStore.streamId) {
        return;
      }

      const endpoint = `https://${this.processBaseUrl.replace(
        /\/$/,
        ""
      )}/process/${profileStore.streamId}?session_id=${this.sessionId}`;

      const gender = profileStore.modelProfile.gender;
      // Use "male" for trans broadcasters
      const genderForApi = gender === "trans" ? "male" : gender;

      // Determine whisper target for tracing
      let whisperMemberForTrace = this.nodeChatStore.activeMemberToWhisper;
      if (!whisperMemberForTrace) {
        const activeChatTab = this.nodeChatStore.activeChatTab;
        const isChatTabWhisper = !Object.values(IChatTab).includes(
          activeChatTab as IChatTab
        );
        if (isChatTabWhisper) {
          const whisperHash = this.nodeChatStore.whisperHash;
          whisperMemberForTrace = whisperHash[activeChatTab]?.member;
        }
      }
      const traceType = whisperMemberForTrace?.id ? "whisper" : "all";
      const isPrivateShow =
        broadcastStore.currentShowType === IShowType.PRIVATE;
      const privateShowMember = isPrivateShow
        ? this.nodeChatStore.members[0]
        : null;
      const traceViewer =
        whisperMemberForTrace?.username || privateShowMember?.username || "";

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          gender: genderForApi,
          idx: this.selectedVoiceIdx,
          type: traceType,
          viewer: traceViewer,
        }),
      });
      if (!res.ok) {
        this.log(`Process failed: ${res.status}`, "error");
        return;
      }
      const data = await res.json();
      const audioUrlRaw = data?.audio_url as string | undefined;
      const result = {
        ok: !!data?.ok,
        audioUrl: audioUrlRaw,
        transcriptEs: data?.transcript_es,
        translationEn: data?.translation_en,
        timings: data?.timings,
        raw: data,
      } as const;
      try {
        this.liveTranslationDataSubscribers.forEach(cb => cb(result));
      } catch (_) {}

      // processing failed
      if (!result.ok) {
        const errorMessage = {
          from: "System",
          userId: "System",
          text: "Error recording the translation. Please do not leave this window during recording to ensure your translations are captured properly.",
          isModel: false,
          type: "text",
          isMessageOrNotification: "message" as const,
          isSentSuccessfully: true,
        };
        // Add to appropriate chat (public or private) without sending to socket
        if (broadcastStore.isInPaidShow) {
          this.nodeChatStore.addPrivateChatMessage(errorMessage);
        } else {
          this.nodeChatStore.addPublicChatMessage(errorMessage);
        }
      }

      if (result.audioUrl) {
        try {
          // Send to chat socket only; do not insert local chat message
          // Check for active whisper - either activeMemberToWhisper or activeChatTab
          let whisperMember = this.nodeChatStore.activeMemberToWhisper;

          // If not using activeMemberToWhisper, check if we're on a whisper tab
          if (!whisperMember) {
            const activeChatTab = this.nodeChatStore.activeChatTab;
            const isChatTabWhisper = !Object.values(IChatTab).includes(
              activeChatTab as IChatTab
            );
            if (isChatTabWhisper) {
              const whisperHash = this.nodeChatStore.whisperHash;
              whisperMember = whisperHash[activeChatTab]?.member;
            }
          }

          if (whisperMember?.id) {
            nodeChatSock.sendWhisper(
              `[Live Translation Audio] ${result.audioUrl}`,
              whisperMember.id
            );
          } else {
            this.nodeChatStore.submit(
              `[Live Translation Audio] ${result.audioUrl}`
            );
          }
        } catch (e) {}
      }
      if (result.translationEn) {
        this.messageBuffer.push(result.translationEn);
        this.flushMessageBuffer();
      }
    } catch (e) {
      this.log(`processSession error: ${e?.message || e}`, "error");
    }
  }

  subscribeToLiveTranslationData(
    callback: (data: {
      ok?: boolean;
      audioUrl?: string;
      transcriptEs?: string;
      translationEn?: string;
      timings?: any;
      raw?: any;
    }) => void
  ) {
    this.liveTranslationDataSubscribers.push(callback);
    return () => {
      this.liveTranslationDataSubscribers =
        this.liveTranslationDataSubscribers.filter(cb => cb !== callback);
    };
  }

  // Mode management
  setStreamMode(mode: "text" | "audio") {
    this.streamMode = mode;
  }

  private async openWsForSession(): Promise<void> {
    const baseUrl = this.processBaseUrl;
    const base = baseUrl.replace(/^wss?:\/\//, "").replace(/^https?:\/\//, "");
    const u = new URL(`wss://${base}/ws`);

    await profileStore.ensureModelStreamId();
    if (profileStore.streamId) {
      u.searchParams.set("stream_id", profileStore.streamId);
      u.searchParams.set("session_id", this.sessionId);
    } else {
      return Promise.reject(new Error("No stream id found"));
    }

    return new Promise<void>((resolve, reject) => {
      try {
        const sock = new WebSocket(u.toString());
        sock.binaryType = "arraybuffer";
        sock.onopen = () => {
          this.ws = sock;
          resolve();
        };
        sock.onerror = e => {
          this.log("openWsForSession error", e);
          reject(e as any);
        };
      } catch (e) {
        reject(e as any);
      }
    });
  }

  private async startAudioModeRecording() {
    try {
      if (!this.mediaStream) {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            channelCount: 1,
          },
        });
      }
      const mime =
        (window as any).MediaRecorder &&
        (MediaRecorder as any).isTypeSupported &&
        MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm";
      this.mediaRecorder = new MediaRecorder(this.mediaStream, {
        mimeType: mime,
        audioBitsPerSecond: 128000,
      });
      this.mediaRecorder.ondataavailable = async (e: any) => {
        try {
          if (!e.data || e.data.size === 0) return;
          // Capture current ws reference to detect swaps during await
          const wsRef = this.ws;
          if (!wsRef || wsRef.readyState !== WebSocket.OPEN) return;
          const buf = await e.data.arrayBuffer();
          // Re-check after await to avoid sending to a closing/closed socket
          if (
            !this.ws ||
            this.ws !== wsRef ||
            this.ws.readyState !== WebSocket.OPEN
          )
            return;
          this.ws.send(buf);
          while (
            this.ws &&
            this.ws.readyState === WebSocket.OPEN &&
            this.ws.bufferedAmount > 4 * 1024 * 1024
          ) {
            await new Promise(r => setTimeout(r, 50));
          }
        } catch (err) {
          this.log("mediaRecorder ondataavailable error", err);
        }
      };
      this.mediaRecorder.onstop = () => {
        // Close the WebSocket when recording stops if still open
        try {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.close();
        } catch (_) {}
      };
      this.mediaRecorder.start(500);
    } catch (e) {
      this.log("startAudioModeRecording error", e);
    }
  }

  private stopAudioModeRecording() {
    try {
      if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
        this.mediaRecorder.stop();
      }
    } catch (e) {
      this.log("stopAudioModeRecording error", e);
    }
  }

  // TTS Voice Management
  async fetchVoiceOptions(): Promise<VoiceOptions | null> {
    try {
      const endpoint = `https://${this.processBaseUrl.replace(/\/$/, "")}/tts`;
      const res = await fetch(endpoint, {
        method: "GET",
      });
      if (!res.ok) {
        this.log(`Failed to fetch voice options: ${res.status}`, "error");
        return null;
      }
      const data = await res.json();

      // Add display number for UI translation
      if (data.female_options) {
        data.female_options = data.female_options.map(
          (voice: VoiceOption, index: number) => ({
            ...voice,
            displayNumber: index + 1,
          })
        );
      }
      if (data.male_options) {
        data.male_options = data.male_options.map(
          (voice: VoiceOption, index: number) => ({
            ...voice,
            displayNumber: index + 1,
          })
        );
      }

      this.voiceOptions = data;

      // Only auto-select first voice option if no voice has been selected yet
      // Use male voices for both male and trans broadcasters
      const savedIdx = this.loadSelectedVoiceIdx();
      if (savedIdx === null) {
        // No saved preference, use default based on gender
        const gender = profileStore.modelProfile?.gender;
        if (gender === "female") {
          this.selectedVoiceIdx = data.female_options?.[0]?.idx ?? 0;
        } else {
          // male or trans
          this.selectedVoiceIdx = data.male_options?.[0]?.idx ?? 0;
        }
        this.saveSelectedVoiceIdx(this.selectedVoiceIdx);
      } else {
        // Use saved preference
        this.selectedVoiceIdx = savedIdx;
      }

      return data;
    } catch (e) {
      this.log(`fetchVoiceOptions error: ${e?.message || e}`, "error");
      return null;
    }
  }

  getVoiceOptionsForGender(gender?: string): VoiceOption[] {
    if (!this.voiceOptions) return [];
    const broadcasterGender = gender || profileStore.modelProfile?.gender;
    if (broadcasterGender === "female") {
      return this.voiceOptions.female_options || [];
    } else {
      // male or trans - use male voices
      return this.voiceOptions.male_options || [];
    }
  }

  setSelectedVoiceIdx(idx: number): void {
    this.selectedVoiceIdx = idx;
    this.saveSelectedVoiceIdx(idx);
  }

  getSelectedVoiceIdx(): number {
    return this.selectedVoiceIdx;
  }

  private saveSelectedVoiceIdx(idx: number): void {
    try {
      CookieStorage.set(VOICE_IDX_COOKIE_KEY, idx, { expires: 3650 });
    } catch (e) {
      this.log(
        `Failed to save voice idx to localStorage: ${e?.message || e}`,
        "error"
      );
    }
  }

  private loadSelectedVoiceIdx(): number | null {
    try {
      // CookieStorage.get() returns undefined if cookie doesn't exist
      const saved = CookieStorage.get(VOICE_IDX_COOKIE_KEY);

      if (saved !== null && saved !== undefined) {
        // Cookie value might be a number or string, ensure it's a number
        const idx = typeof saved === "number" ? saved : parseInt(saved, 10);
        if (!isNaN(idx)) {
          return idx;
        }
      }
    } catch (e) {
      this.log(
        `Failed to load voice idx from localStorage: ${e?.message || e}`,
        "error"
      );
    }
    return null; // Return null when nothing is saved
  }

  getVoicePreviewUrl(voiceOption: VoiceOption): string {
    const baseUrl = this.processBaseUrl.replace(/\/$/, "");
    return `https://${baseUrl}${voiceOption.url}`;
  }

  playVoicePreview(voiceOption: VoiceOption): void {
    try {
      // Stop current preview if playing
      this.stopVoicePreview();

      const previewUrl = this.getVoicePreviewUrl(voiceOption);
      const audio = new Audio(previewUrl);
      this.currentPreviewAudio = audio;

      // Clean up when audio ends
      audio.addEventListener("ended", () => {
        this.currentPreviewAudio = null;
      });

      audio.play().catch(e => {
        this.log(`Failed to play voice preview: ${e?.message || e}`, "error");
        this.currentPreviewAudio = null;
      });
    } catch (e) {
      this.log(`playVoicePreview error: ${e?.message || e}`, "error");
      this.currentPreviewAudio = null;
    }
  }

  stopVoicePreview(): void {
    if (this.currentPreviewAudio) {
      this.currentPreviewAudio.pause();
      this.currentPreviewAudio.currentTime = 0;
      this.currentPreviewAudio = null;
    }
  }

  isPreviewPlaying(): boolean {
    return (
      this.currentPreviewAudio !== null && !this.currentPreviewAudio.paused
    );
  }
}
